import { useEffect, useMemo, useState } from 'react';
import type { Account } from '../../core/accounts';
import type { Category } from '../../core/categories';
import { formatDateKey } from '../../core/dates';
import { EMPTY_FILTERS, activeChips, applyFilters, type Filters } from '../../core/filters';
import { formatPaise } from '../../core/money';
import { periodMonths, type Period } from '../../core/period';
import {
  REPORTS,
  buildReport,
  canUseRollups,
  columnsFor,
  defaultTemplate,
  type Cell,
  type ReportColumn,
  type ReportId,
} from '../../core/reports';
import type { Rollup } from '../../core/rollups';
import type { Transaction } from '../../core/transactions';
import { watchAccounts } from '../../data/accounts';
import { watchCategories, watchTags, type Tag } from '../../data/categories';
import { watchRollups } from '../../data/rollups';
import { logoUrl, watchSettings, DEFAULT_SETTINGS, type AppSettings } from '../../data/settings';
import { queryForFilters, queryKey, watchTransactions } from '../../data/transactions';
import { FilterBar } from '../components/FilterBar';
import { EXPORT_FORMATS, buildExport, saveBlob, type ExportFormat } from '../reports/download';
import type { Template } from '../reports/exportPdf';

/** Rows drawn on screen. The export always carries the whole set (FR-RPT-09). */
const PREVIEW_ROWS = 200;

interface Props {
  period: Period;
}

/**
 * Reports (FR-RPT-03/04/05/07), desktop-first by FR-RPT-15.
 *
 * The screen is deliberately thin: it chooses a report, hands the filtered set to
 * `core/reports.ts`, renders whatever comes back, and passes the same object to the
 * exporter. Adding a report means adding a builder, not touching this file — which is
 * FR-RPT-02.
 */
export function ReportsScreen({ period }: Props) {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [settings, setSettings] = useState<AppSettings>(DEFAULT_SETTINGS);
  const [transactions, setTransactions] = useState<Transaction[] | null>(null);
  const [rollups, setRollups] = useState<Map<string, Rollup>>(new Map());

  const [reportId, setReportId] = useState<ReportId>('detailed');
  const [filters, setFilters] = useState<Filters>(() => EMPTY_FILTERS(period));
  const [template, setTemplate] = useState<Template | null>(null);
  const [exporting, setExporting] = useState<ExportFormat | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => watchAccounts(setAccounts), []);
  useEffect(() => watchCategories(setCategories), []);
  useEffect(() => watchTags(setTags), []);
  useEffect(() => watchSettings(setSettings), []);
  useEffect(() => setFilters((f) => ({ ...f, period })), [period]);

  const specKey = queryKey(queryForFilters(filters));
  const spec = useMemo(() => queryForFilters(filters), [specKey]);
  useEffect(() => {
    setTransactions(null);
    return watchTransactions(
      spec,
      (list) => {
        setTransactions(list);
        setError(null);
      },
      (cause) => {
        setTransactions([]);
        setError(cause.message);
      },
    );
  }, [spec]);

  // FR-RPT-16: rollups stand in where the grouping matches. Twelve documents for a year,
  // regardless of how many transactions are behind them.
  const months = useMemo(() => periodMonths(filters.period), [filters.period]);
  const monthsKey = months.join(',');
  useEffect(() => watchRollups(months, setRollups), [monthsKey]);

  const accountType = useMemo(() => {
    const map = new Map(accounts.map((a) => [a.id, a.type]));
    return (id: string) => map.get(id);
  }, [accounts]);

  const visible = useMemo(
    () => (transactions ? applyFilters(transactions, filters) : []),
    [transactions, filters],
  );

  const nameOf = (list: { id: string; name: string }[]) => (id: string) =>
    list.find((x) => x.id === id)?.name ?? id;

  const appliedFilters = useMemo(
    () =>
      activeChips(filters, {
        account: nameOf(accounts),
        category: nameOf(categories),
        tag: nameOf(tags),
        money: (paise) => formatPaise(paise),
      }).map((chip) => chip.label),
    [filters, accounts, categories, tags],
  );

  const report = useMemo(
    () =>
      buildReport(
        reportId,
        visible,
        {
          filters,
          accountName: nameOf(accounts),
          categoryName: nameOf(categories),
          accountType,
          appliedFilters,
        },
        [...rollups.values()],
      ),
    [reportId, visible, filters, accounts, categories, accountType, appliedFilters, rollups],
  );

  // FR-RPT-14: auto-selected by the business filter, overridable here.
  const effectiveTemplate = template ?? defaultTemplate(filters);
  const columns = columnsFor(report, effectiveTemplate);

  async function exportAs(format: ExportFormat) {
    setError(null);
    setExporting(format);
    try {
      // Fetched as a data URI at export time rather than held in state: a Storage
      // download URL expires, and pdfmake needs the bytes, not a link.
      const branding = {
        personalName: settings.personalName,
        businessName: settings.businessName,
        businessAddress: settings.businessAddress,
        personalLogo:
          effectiveTemplate === 'personal' ? await dataUri(settings.personalLogoPath) : null,
        businessLogo:
          effectiveTemplate === 'business' ? await dataUri(settings.businessLogoPath) : null,
      };

      const { blob, filename } = await buildExport({
        report,
        format,
        template: effectiveTemplate,
        branding,
        generatedOn: new Date(),
      });
      saveBlob(blob, filename);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not build that export.');
    } finally {
      setExporting(null);
    }
  }

  const definition = REPORTS.find((r) => r.id === reportId);

  return (
    <>
      <div className="head-row">
        <h1>Reports</h1>
        <span className="muted tiny">
          {report.source === 'rollups'
            ? 'From monthly totals'
            : `From ${visible.length} transaction${visible.length === 1 ? '' : 's'}`}
        </span>
      </div>

      <div className="report-picker" role="group" aria-label="Report">
        {REPORTS.map((r) => (
          <button
            key={r.id}
            type="button"
            className="report-tab"
            aria-pressed={reportId === r.id}
            onClick={() => setReportId(r.id)}
          >
            {r.title}
          </button>
        ))}
      </div>
      {definition && <p className="muted tiny">{definition.description}</p>}

      <FilterBar
        filters={filters}
        accounts={accounts}
        categories={categories}
        tags={tags}
        onChange={setFilters}
      />

      {error && <p className="error">{error}</p>}

      {transactions === null ? (
        <p className="muted">Loading…</p>
      ) : (
        <>
          <div className="table-scroll">
            <table className="table report-table">
              <thead>
                <tr>
                  {columns.map((c) => (
                    <th key={c.key} className={isNumeric(c) ? 'num' : undefined}>
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.rows.slice(0, PREVIEW_ROWS).map((row, i) => (
                  <tr key={i}>
                    {columns.map((c) => (
                      <td key={c.key} className={isNumeric(c) ? 'num' : undefined}>
                        {display(row.cells[c.key] ?? null, c)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              {report.totals && (
                <tfoot>
                  <tr>
                    {columns.map((c) => (
                      <td key={c.key} className={isNumeric(c) ? 'num' : undefined}>
                        <strong>{display(report.totals!.cells[c.key] ?? null, c)}</strong>
                      </td>
                    ))}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {report.rows.length === 0 && (
            <div className="placeholder">Nothing matched these filters.</div>
          )}
          {report.rows.length > PREVIEW_ROWS && (
            <p className="muted tiny">
              Showing the first {PREVIEW_ROWS} of {report.rows.length}. The export carries all{' '}
              {report.rows.length}.
            </p>
          )}
        </>
      )}

      {/* The export bar restates what is about to leave, because FR-RPT-09 makes the
          export and the screen the same set — and the only way to trust that is to see
          it stated in one place. */}
      <div className="export-bar">
        <div className="row">
          <strong>Export</strong>
          {EXPORT_FORMATS.map((f) => (
            <button
              key={f.id}
              type="button"
              className="btn"
              title={f.note}
              disabled={exporting !== null}
              onClick={() => void exportAs(f.id)}
            >
              {exporting === f.id ? 'Preparing…' : f.label}
            </button>
          ))}

          <span className="spacer" />

          <label className="inline">
            Template
            <select
              value={effectiveTemplate}
              onChange={(e) => setTemplate(e.target.value as Template)}
              aria-label="PDF template"
            >
              <option value="personal">Personal — {settings.personalName}</option>
              <option value="business">Business — {settings.businessName}</option>
            </select>
          </label>
        </div>

        <p className="muted tiny">
          {report.title} · {report.periodLabel} ·{' '}
          {appliedFilters.length === 0 ? 'no filters beyond the period' : appliedFilters.join(' · ')}
          {' · '}
          {report.rows.length} row{report.rows.length === 1 ? '' : 's'}
          {template !== null && ' · template overridden'}
          {!canUseRollups(filters) && report.source === 'transactions' && report.id !== 'detailed' && (
            <> · filtered, so counted from transactions rather than monthly totals</>
          )}
        </p>
      </div>
    </>
  );
}

function isNumeric(column: ReportColumn): boolean {
  return column.type === 'money' || column.type === 'number' || column.type === 'percent';
}

/** On-screen formatting only. The model keeps paise so exports stay numeric. */
function display(value: Cell, column: ReportColumn): string {
  if (value === null || value === undefined || value === '') return '';
  switch (column.type) {
    case 'date':
      return formatDateKey(String(value), 'dd MMM yyyy');
    case 'money':
      return formatPaise(Number(value), { symbol: false });
    case 'percent':
      return `${(Number(value) / 100).toFixed(1)}%`;
    default:
      return String(value);
  }
}

async function dataUri(path: string | null): Promise<string | null> {
  const url = await logoUrl(path);
  if (!url) return null;
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(new Error('Could not read the logo.'));
      reader.readAsDataURL(blob);
    });
  } catch {
    // A missing logo must not stop an export. The document simply has no mark.
    return null;
  }
}
